/**
 * SettingInput — renders an editable input for a single system_settings row.
 *
 * Dispatches by `valueType`:
 *   - boolean → checkbox-based switch
 *   - number  → `<input type="number">`
 *   - string  → `<input type="text">` (or `type="password"` when `isSecret`)
 *   - json    → `<textarea>` with inline JSON.parse validation
 *
 * Secret values from the server arrive as `'***'`. When the user types into a
 * secret input, we treat it as a "new secret" — the server replaces the
 * whole value. The form does NOT surface the raw old value (we never receive
 * it).
 */

import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

export type WireValueType = 'string' | 'number' | 'boolean' | 'json';

export interface SettingInputProps {
  settingKey: string;
  value: unknown;
  valueType: WireValueType;
  isSecret: boolean;
  description: string | null;
  disabled?: boolean;
  submitting?: boolean;
  onSave: (value: unknown) => void;
}

/** Best-effort JSON stringify used to seed the textarea from `value`. */
function toTextarea(value: unknown): string {
  if (value === undefined || value === null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

export function SettingInput({
  settingKey,
  value,
  valueType,
  isSecret,
  description,
  disabled,
  submitting,
  onSave,
}: SettingInputProps) {
  const [draft, setDraft] = useState<string>(
    valueType === 'json' ? toTextarea(value) : String(value ?? ''),
  );
  const [boolDraft, setBoolDraft] = useState<boolean>(Boolean(value));
  const [jsonError, setJsonError] = useState<string | null>(null);

  const isDirty =
    valueType === 'boolean'
      ? boolDraft !== Boolean(value)
      : valueType === 'json'
        ? draft !== toTextarea(value)
        : draft !== String(value ?? '');

  function handleJsonChange(next: string) {
    setDraft(next);
    if (next.trim().length === 0) {
      setJsonError(null);
      return;
    }
    try {
      const parsed = JSON.parse(next) as unknown;
      if (parsed === null || (typeof parsed !== 'object' && !Array.isArray(parsed))) {
        setJsonError('O valor deve ser um objeto ou array JSON.');
        return;
      }
      setJsonError(null);
    } catch (err) {
      setJsonError(err instanceof Error ? err.message : 'JSON inválido.');
    }
  }

  function handleSave() {
    if (valueType === 'boolean') {
      onSave(boolDraft);
      return;
    }
    if (valueType === 'number') {
      const n = Number(draft);
      if (!Number.isFinite(n)) {
        setJsonError('Número inválido.');
        return;
      }
      onSave(n);
      return;
    }
    if (valueType === 'string') {
      onSave(draft);
      return;
    }
    if (valueType === 'json') {
      try {
        onSave(JSON.parse(draft));
      } catch (err) {
        setJsonError(err instanceof Error ? err.message : 'JSON inválido.');
      }
    }
  }

  const label = (
    <div className="flex flex-col">
      <label htmlFor={`setting-${settingKey}`} className="font-mono text-xs text-muted-foreground">
        {settingKey}
      </label>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
  );

  const canSave = !jsonError && isDirty && !disabled && !submitting;

  return (
    <div
      className="flex flex-col gap-2 border-b py-3 last:border-b-0"
      data-testid={`setting-${settingKey}`}
    >
      {label}

      <div className="flex items-center gap-2">
        {valueType === 'boolean' ? (
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              id={`setting-${settingKey}`}
              type="checkbox"
              aria-label={settingKey}
              checked={boolDraft}
              onChange={(e) => setBoolDraft(e.target.checked)}
              disabled={disabled || submitting}
              className="h-4 w-4 rounded border-input"
            />
            <span>{boolDraft ? 'Ativado' : 'Desativado'}</span>
          </label>
        ) : valueType === 'number' ? (
          <Input
            id={`setting-${settingKey}`}
            type="number"
            aria-label={settingKey}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={disabled || submitting}
          />
        ) : valueType === 'string' ? (
          <Input
            id={`setting-${settingKey}`}
            type={isSecret ? 'password' : 'text'}
            aria-label={settingKey}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={disabled || submitting}
            placeholder={isSecret ? '••• (mascarado — substitui por completo)' : undefined}
          />
        ) : (
          <textarea
            id={`setting-${settingKey}`}
            aria-label={settingKey}
            value={draft}
            onChange={(e) => handleJsonChange(e.target.value)}
            disabled={disabled || submitting}
            rows={5}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-xs"
          />
        )}
        <Button
          type="button"
          size="sm"
          onClick={handleSave}
          disabled={!canSave}
          aria-label={`Salvar ${settingKey}`}
        >
          Salvar
        </Button>
      </div>
      {jsonError ? (
        <p className="text-sm text-destructive" role="alert">
          {jsonError}
        </p>
      ) : null}
    </div>
  );
}
